import React from "react";
import { Link } from "react-router-dom";

/**
 * Terms of Service Page
 *
 * Simple, honest terms for BibeCheck
 */
const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Back Link - Top */}
        <div className="mb-8">
          <Link
            to="/"
            className="text-purple-600 hover:text-purple-700 font-medium underline inline-flex items-center gap-1"
          >
            <span>←</span> Back to BibeCheck
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            BibeCheck
          </h1>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Terms of Service
          </h2>
          <p className="text-gray-600">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 border border-gray-200">
          <div className="prose prose-gray max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Welcome to BibeCheck
              </h3>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") govern your use of BibeCheck,
                an interactive Bible exploration platform built by Jeyner Gil
                Caga. By using BibeCheck, you agree to these terms. If you don't
                agree, please don't use BibeCheck.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Plain English:</strong> These are the rules for using
                BibeCheck. We keep them simple and fair.
              </p>
            </section>

            {/* What BibeCheck Is */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                What BibeCheck Is (And Isn't)
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>BibeCheck is:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  A tool to help you explore and understand the Bible through
                  AI-powered questions and visual connections
                </li>
                <li>
                  An educational platform for curious learners, beginners, and
                  Bible students
                </li>
                <li>Built by a solo developer with a mission to make Scripture accessible</li>
              </ul>

              <p className="text-gray-700 leading-relaxed mt-6 mb-4">
                <strong>BibeCheck is NOT:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  A replacement for reading the Bible, attending church, or
                  spiritual guidance
                </li>
                <li>
                  A source of theological authority — always verify important
                  doctrinal questions with trusted spiritual leaders
                </li>
                <li>
                  Perfect — it's built by a human and uses AI, both of which
                  can make mistakes
                </li>
              </ul>
            </section>

            {/* User Obligations */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Your Responsibilities as a User
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When using BibeCheck, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Be Respectful:</strong> Use BibeCheck for genuine
                  learning and exploration
                </li>
                <li>
                  <strong>Keep Your Account Secure:</strong> Don't share your
                  login credentials
                </li>
                <li>
                  <strong>Provide Accurate Information:</strong> Give us a real
                  email address and payment information
                </li>
                <li>
                  <strong>Follow the Law:</strong> Don't use BibeCheck for
                  illegal purposes
                </li>
                <li>
                  <strong>Don't Abuse the Service:</strong> No spamming,
                  hacking, or overwhelming the system
                </li>
                <li>
                  <strong>Be 13 or Older:</strong> BibeCheck is not for children
                  under 13
                </li>
              </ul>
            </section>

            {/* Pricing & Payment - COMMENTED OUT UNTIL FINALIZED */}
            {/*
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Pricing, Payment & Billing
              </h3>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                Founding Member Pricing
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Price:</strong> Founding members pay $19.99/month,
                  locked in for life
                </li>
                <li>
                  <strong>Billing:</strong> Charged monthly via Stripe
                </li>
                <li>
                  <strong>Trial Period:</strong> Billing starts when BibeCheck
                  launches
                </li>
                <li>
                  <strong>Lifetime Lock:</strong> Your founding member price
                  never changes, even if we raise prices later
                </li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                Payment Processing
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>All payments are processed securely through Stripe</li>
                <li>
                  We never store or have access to your credit card details
                </li>
                <li>
                  You'll receive an email receipt for every payment
                </li>
                <li>
                  Failed payments may result in temporary suspension of access
                </li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                Price Changes
              </h4>
              <p className="text-gray-700 leading-relaxed">
                We may change prices for new customers in the future. However,
                <strong> founding members' prices are locked forever</strong> —
                your $19.99/month rate will never increase as long as you
                maintain your subscription.
              </p>
            </section>
            */}

            {/* Cancellation & Refunds */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Cancellation Policy
              </h3>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                How to Cancel
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>You can cancel your subscription anytime from your account settings</li>
                <li>Or email us at jeyner@bibecheck.com and we'll cancel it for you</li>
                <li>No cancellation fees, no questions asked</li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                What Happens When You Cancel
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  You keep access until the end of your current billing period
                </li>
                <li>You won't be charged again</li>
                <li>
                  Your data remains for 30 days in case you change your mind
                </li>
                <li>After 30 days, your data is permanently deleted</li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                Refund Policy
              </h4>
              <div className="bg-yellow-50 rounded-lg p-6 border-l-4 border-yellow-600">
                <p className="text-gray-900 font-semibold mb-2">
                  Honest Disclosure About Refunds
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  BibeCheck is built by a bootstrapped solo developer with no
                  external funding. Stripe (our payment processor) charges fees
                  on all transactions, <strong>including refunds</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Our policy:</strong> Instead of offering refunds, we
                  extend your trial period if there are delays. If you cancel
                  before launch, you're charged $0. If you're unhappy after
                  launch, you can cancel anytime with no further charges.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Why?</strong> Refunds cost us money we can't afford to
                  lose. We'd rather extend your trial than lose money on fees.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Who Owns What
              </h3>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                BibeCheck Owns
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>The BibeCheck platform, code, design, and features</li>
                <li>The BibeCheck name and branding</li>
                <li>Any content we create (tutorials, help docs, etc.)</li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                You Own
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>The questions you ask and explorations you create</li>
                <li>
                  Any videos or content you create using BibeCheck (Creator tier)
                </li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                The Bible
              </h4>
              <p className="text-gray-700 leading-relaxed">
                The Bible text itself is public domain. Different translations
                may have copyright restrictions. BibeCheck uses public domain
                translations and provides access to licensed translations where
                applicable.
              </p>
            </section>

            {/* AI Disclaimer */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                AI-Generated Content Disclaimer
              </h3>
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                <p className="text-gray-700 leading-relaxed mb-3">
                  BibeCheck uses AI (artificial intelligence) to answer your
                  Bible questions and create connections. While we strive for
                  accuracy, <strong>AI can make mistakes</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Important:</strong> AI responses should not be treated
                  as theological authority. Always verify important doctrinal
                  questions with trusted spiritual leaders, pastors, or scholars.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  BibeCheck is a <strong>learning tool</strong>, not a
                  replacement for traditional Bible study, spiritual guidance, or
                  church community.
                </p>
              </div>
            </section>

            {/* Limitations */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Limitations of Liability
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Plain English:</strong> BibeCheck is provided "as is."
                We're a solo developer doing our best, but we can't guarantee
                perfection.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  BibeCheck may have bugs, errors, or downtime — we'll fix them
                  as fast as we can
                </li>
                <li>
                  AI responses may contain inaccuracies — always verify
                  important information
                </li>
                <li>
                  We're not liable for decisions you make based on BibeCheck
                  content
                </li>
                <li>
                  Maximum liability is limited to the amount you paid in the last
                  12 months
                </li>
              </ul>
            </section>

            {/* Account Termination */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Account Termination
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We hope this never happens, but we reserve the right to suspend
                or terminate accounts that:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Violate these Terms of Service</li>
                <li>Abuse or overwhelm the system</li>
                <li>Engage in illegal activity</li>
                <li>Have payment issues (after multiple failed attempts)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>We'll always try to contact you first</strong> and give
                you a chance to fix the issue before terminating your account.
              </p>
            </section>

            {/* Changes to Service */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Changes to BibeCheck
              </h3>
              <p className="text-gray-700 leading-relaxed">
                BibeCheck is actively being developed. We may add new features,
                change existing features, or retire features over time. We'll do
                our best to notify you of major changes, but small improvements
                happen continuously.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Changes to These Terms
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms as BibeCheck grows. If we make
                significant changes, we'll email you at least 30 days before they
                take effect. Continuing to use BibeCheck after changes means you
                accept the new terms.
              </p>
            </section>

            {/* Dispute Resolution */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Dispute Resolution
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have a problem with BibeCheck:
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Email me first:</strong> jeyner@bibecheck.com — I read
                  every email personally and will do my best to resolve any issue
                </li>
                <li>
                  <strong>If we can't resolve it:</strong> Any disputes will be
                  governed by the laws of Singapore
                </li>
              </ol>
              <p className="text-gray-700 leading-relaxed mt-4">
                Honestly, I'd much rather work with you to fix any problems than
                deal with legal stuff. Just email me.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Questions About These Terms?
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                BibeCheck is built by a solo developer. If you have questions
                about these terms or anything else, email me:
              </p>
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                <p className="text-gray-900 font-medium">Jeyner Gil Caga</p>
                <p className="text-gray-700">Founder, BibeCheck</p>
                <p className="text-blue-600 font-medium mt-2">
                  <a
                    href="mailto:jeyner@bibecheck.com"
                    className="hover:underline">
                    jeyner@bibecheck.com
                  </a>
                </p>
              </div>
            </section>

            {/* Simple Summary */}
            <section className="mt-12 p-6 bg-purple-50 rounded-lg border-l-4 border-purple-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Terms of Service in Plain English
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>✅ BibeCheck is a learning tool, not theological authority</li>
                {/* <li>✅ Founding members pay $19.99/mo, locked for life</li> */}
                <li>✅ Cancel anytime, no cancellation fees</li>
                {/* <li>✅ No refunds, but we extend trials if launch is delayed</li> */}
                <li>✅ AI can make mistakes — verify important info</li>
                <li>✅ Be respectful, don't abuse the service</li>
                <li>✅ You own your content, we own the platform</li>
                <li>✅ Email me if you have problems</li>
              </ul>
            </section>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="text-purple-600 hover:text-purple-700 font-medium underline">
            ← Back to BibeCheck
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
